// src/pages/ExhaustSystems.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MediaCarousel from "../components/MediaCarousel";      // version with ‹ / › + onSelect
import SearchFilters from "../components/SearchFilters";      // the filter bar you created
import { ALL_PRODUCTS } from "../data/exhaustProducts";       // derived make/model list

// Inline helper: discover sequential files like /assets/exh_clip01.mp4 ...
async function loadSequentialVideos({ base = "/assets/exh_clip", start = 1, end = 50, pad = 2, ext = ".mp4" }) {
  const exists = async (url) => {
    try {
      const res = await fetch(url, { method: "HEAD" });
      return res.ok;
    } catch {
      return false;
    }
  };
  const found = [];
  for (let i = start; i <= end; i++) {
    const num = String(i).padStart(pad, "0"); // 01, 02, ...
    const url = `${base}${num}${ext}`;
    if (await exists(url)) found.push({ id: `v${i}`, type: "video", src: url, name: `Clip ${i}` });
  }
  return found;
}

export default function ExhaustSystems() {
  // Hero video (left big player)
  const [heroSrc, setHeroSrc] = useState("/assets/exhaust-hero.mp4");

  // Carousel thumbnails (auto-filled)
  const [carouselMedia, setCarouselMedia] = useState([]);

  // Make/Model filters
  const [filters, setFilters] = useState({ make: "", model: "" });

  // Load media automatically: /public/assets/exh_clip01.mp4, exh_clip02.mp4, ...
  useEffect(() => {
    (async () => {
      const vids = await loadSequentialVideos({
        base: "/assets/exh_clip", // change to "/assets/dp_clip" on the Downpipes page
        start: 1,
        end: 50,
        pad: 2,
        ext: ".mp4",
      });
      setCarouselMedia(vids);
      // Optionally set first clip as hero:
      // if (vids.length) setHeroSrc(vids[0].src);
    })();
  }, []);

  // Apply filters to ALL_PRODUCTS (exhaust + future downpipes once added there)
  const filtered = ALL_PRODUCTS.filter((p) => {
    const byMake = !filters.make || p.make === filters.make;
    const byModel = !filters.model || p.model === filters.model;
    return byMake && byModel;
  });

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 text-neutral-900">
      {/* Top: video left, text right */}
      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* Left hero video */}
        <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-sm bg-black">
          <video
            key={heroSrc}                // reloads when src changes
            src={heroSrc}
            controls
            playsInline
            className="w-full h-[420px] object-cover"
          />
        </div>

        {/* Right text + CTA + Carousel */}
        <div>
          <h1 className="text-3xl font-bold mb-4">Unlimited Levels Exhaust Systems</h1>
          <p className="text-lg text-neutral-700 mb-6">
            Designed for maximum flow, precision fitment, and a tone that turns heads.
            Handcrafted with premium materials and TIG welds—built for daily driving and track use.
            Choose Stainless Steel for a deeper roar, or Titanium for F1-style sound.
          </p>

          <Link
            to="/request-exhaust-quote"
            className="inline-flex items-center px-5 py-3 rounded-xl bg-black text-white font-semibold hover:bg-neutral-800 transition"
          >
            Request a Quote
          </Link>

          <div className="mt-10">
            <h3 className="text-xl font-semibold mb-3">Media</h3>
            <MediaCarousel
              items={carouselMedia}
              onSelect={(m) => {
                if (m.type === "video") setHeroSrc(m.src);
              }}
            />
          </div>
        </div>
      </div>

      {/* Product grid + filters */}
      <div className="mt-14">
        <h2 className="text-2xl font-bold mb-3">Shop</h2>

        {/* Make/Model Filters */}
        <SearchFilters
          allProducts={ALL_PRODUCTS}
          value={filters}
          onChange={setFilters}
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="group relative bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden"
            >
              <img
                src={p.img}
                alt={p.name}
                className="w-full h-48 object-cover transition duration-300 group-hover:scale-[1.03]"
              />

              <div className="p-4 flex flex-col gap-1">
                <div className="font-semibold">{p.name}</div>
                {/* You set `car` to "Starting at" — keep that label next to the price */}
                <div className="text-sm text-neutral-600">{p.car}</div>
                <div className="mt-2 font-bold">
                  ${((p.basePrice ?? 0)).toLocaleString()}
                </div>
                {p.category && (
                  <div className="mt-1 text-xs uppercase tracking-wide text-neutral-500">
                    {p.category}
                  </div>
                )}
              </div>

              {/* View button to product page */}
              <Link
                to={`/exhaust/${p.slug}`}
                className="absolute bottom-3 right-3 px-3 py-2 rounded-lg bg-black text-white text-sm font-semibold hover:bg-neutral-800 transition"
              >
                View
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
